import { createSlice } from '@reduxjs/toolkit';
import { isNative } from '../../utils/webOnly';

const initialState = {
    audioList: [], // List of audio tracks
    autoPlay: false, // Whether the player should autoplay
    playMode: 'order', // Play mode: 'order', 'orderLoop', 'singleLoop', 'shufflePlay'
    mode: 'full', // Player mode: 'mini' or 'full',
    currentPlayId: 0,
    clearPriorAudioLists: true, // Whether player should replace or append on next audioList change
    autoRadio: true, // Auto-discover mode: fetch recommendations when queue ends
    playedTrackKeys: [], // Track keys already played in this radio session (to avoid repeats)
    likesPlayback: false, // Whether current queue is from "Play All Likes"
    likesPlaybackOffset: 0, // Next offset to fetch from likes
    queueOwner: null, // Surface that built the current queue (see setQueueOwner)

    // ── Mobile Now-Playing page support ──
    // `currentTrack` mirrors the jinke audioInfo of the track that's playing
    // right now (populated from MusicPlayer.js's onAudioPlay). `isPlaying`
    // toggles on play/pause. `expanded` controls whether the full-screen
    // Now Playing page is mounted over the app chrome on mobile.
    currentTrack: null,
    isPlaying: false,
    expanded: false,

    // ── Native plugin state mirror (Capacitor) ──
    // `position` + `duration` are written from the native plugin's
    // `playerState` event (4Hz while playing + transitions). They mirror
    // AVPlayer/ExoPlayer's clock so the React UI can read fresh position
    // without polling jinke's HTML5 element (which freezes in background
    // on iOS WKWebView). On web/Electron these stay at 0.
    position: 0,
    duration: 0,
    // URL of the item the native plugin has loaded (from playerState). Null
    // on web/Electron.
    currentUrl: null,
};

// Helper function to save state to localStorage (not electron-store to avoid 318MB rewrite)
const saveToStore = (state) => {
    try {
        localStorage.setItem('musicPlayer', JSON.stringify({
            playMode: state.playMode,
            mode: state.mode,
            currentPlayId: state.currentPlayId,
            autoRadio: state.autoRadio,
        }));
    } catch (error) {
        // Silently fail - playlist persistence is not critical
    }
};

const musicPlayerSlice = createSlice({
    name: 'musicPlayer',
    initialState,
    reducers: {
        // Set the audio list — replaces the queue. Re-arms clearPriorAudioLists
        // so jinke restarts with the new list rather than appending to whatever
        // it had before (a prior addTrack may have flipped the flag to false).
        setAudioList(state, action) {
            // Filter out any tracks with invalid musicSrc
            const validTracks = action.payload.filter(track => {
                if (!track.musicSrc || track.musicSrc === '' || track.musicSrc === 'undefined') {
                    console.warn('[musicPlayerSlice] Filtering out track with invalid musicSrc:', track);
                    return false;
                }
                // Filter out DASH manifests
                if (track.musicSrc.includes('.mpd') || track.musicSrc.includes('manifest.mpd')) {
                    console.warn('[musicPlayerSlice] Filtering out DASH manifest:', track.musicSrc);
                    return false;
                }
                try {
                    new URL(track.musicSrc);
                    return true;
                } catch (e) {
                    console.warn('[musicPlayerSlice] Filtering out track with malformed URL:', track.musicSrc);
                    return false;
                }
            });
            state.audioList = validTracks;
            state.clearPriorAudioLists = true;
            // Whoever rebuilds the queue owns it now — likes playback is over.
            // Without this the flag stayed true forever after Play All Likes:
            // the likes refill kept appending into other surfaces' queues, and
            // the likes Play All button "resumed" whatever random track was
            // loaded instead of starting the likes. (The likes flow itself
            // re-sets the flag right after its reset — order matters there.)
            state.likesPlayback = false;
            state.likesPlaybackOffset = 0;
            state.queueOwner = null;
        },

        // Add a track to the audio list
        addTrack(state, action) {
            // If list already has tracks, this is an append (queue) — don't clear the player.
            // If list is empty (after resetAudioList), this is "play now" — keep clear flag.
            if (state.audioList.length > 0) {
                state.clearPriorAudioLists = false;
            }
            let track = action.payload;

            // Validate track has required fields and valid musicSrc
            if (!track.musicSrc || track.musicSrc === '' || track.musicSrc === 'undefined') {
                console.error('[musicPlayerSlice] Invalid track - missing or empty musicSrc:', track);
                return; // Don't add invalid tracks
            }

            // Check for DASH manifest (not supported by HTML5 audio)
            if (track.musicSrc.includes('.mpd') || track.musicSrc.includes('manifest.mpd')) {
                console.error('[musicPlayerSlice] Invalid track - DASH manifest not supported:', track.musicSrc);
                return; // Don't add DASH streams
            }

            // Validate musicSrc is a valid URL
            try {
                new URL(track.musicSrc);
            } catch (e) {
                console.error('[musicPlayerSlice] Invalid track - malformed URL:', track.musicSrc);
                return; // Don't add tracks with invalid URLs
            }

            // Uniquify duplicate srcs with a `#qN` fragment (never sent over
            // the network). jinke both DROPS appended entries whose musicSrc
            // already exists in its internal list and DELETES by key lookup
            // over musicSrc matches — so a second copy of the same track
            // either never played or vanished together with the first when
            // one was removed. Done here (not in callers) so every add path
            // is covered; utils/queueAdd.js delegates to this.
            if (state.audioList.some(t => t.musicSrc === track.musicSrc)) {
                let n = 2;
                while (state.audioList.some(t => t.musicSrc === `${track.musicSrc}#q${n}`)) n++;
                track = { ...track, musicSrc: `${track.musicSrc}#q${n}` };
            }

            state.audioList.push(track);
        },

        // Upgrade a queued track's musicSrc to a stream URL (for lazy full-play extraction)
        upgradeTrackSrc(state, action) {
            const { oldMusicSrc, newMusicSrc, originalUrl } = action.payload;
            const track = state.audioList.find(t => t.musicSrc === oldMusicSrc);
            if (track) {
                track.musicSrc = newMusicSrc;
                if (originalUrl) track.originalUrl = originalUrl;
            }
        },

        // A /lazy-audio search resolved a YouTube link for a queue entry that
        // was added without one — patch every matching entry so the waveform
        // (originalUrl) and feed-row matching work.
        upgradeTrackLink(state, action) {
            const { marker, youtubeId, originalUrl } = action.payload;
            for (const t of state.audioList) {
                if (t.musicSrc && t.musicSrc.includes(marker)) {
                    t.youtubeId = youtubeId;
                    t.originalUrl = originalUrl;
                }
            }
        },

        // Mirror the player library's internal list after a library-side
        // delete (queue-panel remove routes through jinke so its internal
        // list and ours can't diverge). Payload = the reconciled list.
        // Does NOT touch clearPriorAudioLists — this is a mirror, not a
        // "play now" replacement.
        syncQueueFromPlayer(state, action) {
            state.audioList = action.payload;
        },

        // Remove a track from the audio list by index
        removeTrack(state, action) {
            const index = action.payload;
            if (index >= 0 && index < state.audioList.length) {
                state.audioList.splice(index, 1);
            } else {
                console.warn(`Invalid index: ${index}`);
            }
        },

        // Reset the audio list to empty (next addTrack after this will clear the player)
        resetAudioList(state) {
            state.audioList = [];
            state.clearPriorAudioLists = true;
            // See setAudioList: a queue reset means a new owner (or nothing)
            // is taking over — likes playback must not survive it.
            state.likesPlayback = false;
            state.likesPlaybackOffset = 0;
            state.queueOwner = null;
        },

        // Toggle autoplay
        toggleAutoPlay(state) {
            state.autoPlay = !state.autoPlay;
        },

        // Set autoplay directly
        setAutoPlay(state, action) {
            state.autoPlay = action.payload;
        },

        // Set the play mode
        setPlayMode(state, action) {
            const validModes = ['order', 'orderLoop', 'singleLoop', 'shufflePlay'];
            if (validModes.includes(action.payload)) {
                state.playMode = action.payload;
            } else {
                console.warn(`Invalid play mode: ${action.payload}`);
            }
        },

        // Set the player mode
        setMode(state, action) {
            const validModes = ['mini', 'full'];
            if (validModes.includes(action.payload)) {
                state.mode = action.payload;
            } else {
                console.warn(`Invalid player mode: ${action.payload}`);
            }
        },

        // Toggle auto-radio mode
        toggleAutoRadio(state) {
            state.autoRadio = !state.autoRadio;
            if (!state.autoRadio) {
                state.playedTrackKeys = [];
            }
            saveToStore(state);
        },

        setAutoRadio(state, action) {
            state.autoRadio = action.payload;
            if (!action.payload) {
                state.playedTrackKeys = [];
            }
            saveToStore(state);
        },

        // Mark a track as played (for radio mode dedup)
        addPlayedTrackKey(state, action) {
            const key = action.payload;
            if (!state.playedTrackKeys.includes(key)) {
                state.playedTrackKeys.push(key);
            }
        },

        clearPlayedTracks(state) {
            state.playedTrackKeys = [];
        },

        // Which surface built the current queue — {type: 'playlist'|'likes'|
        // 'feed'|'list', id?} or null. Set by that surface's Play All handler
        // AFTER its setAudioList/resetAudioList (which clear it), read by the
        // same surface's Play All button to show an honest pause/resume state
        // (a playlist button must never show "Pause" while the feed plays).
        setQueueOwner(state, action) {
            state.queueOwner = action.payload || null;
        },

        setLikesPlayback(state, action) {
            state.likesPlayback = action.payload;
            if (!action.payload) {
                state.likesPlaybackOffset = 0;
            }
        },

        setLikesPlaybackOffset(state, action) {
            state.likesPlaybackOffset = action.payload;
        },

        // Reset the player state to initial
        resetPlayerState(state) {
            Object.assign(state, initialState);
        },

        // Save playlist to storage
        savePlaylist(state) {
            saveToStore(state);
        },

        // ── Mobile Now-Playing page support ──
        setCurrentTrack(state, action) {
            // On native, jinke (muted HTML5 audio) and the native plugin
            // both fire onAudioPlay-style events as their respective audio
            // elements load/loop. Letting both write currentTrack causes
            // it to flip back to jinke's stale audioList[0] every time
            // jinke's muted audio loops. Native plugin's `playerState` ->
            // `setNativePlayerState` is the authoritative source on
            // native; setCurrentTrack is for web/Electron only.
            if (isNative) return;
            state.currentTrack = action.payload;
        },
        setIsPlaying(state, action) {
            state.isPlaying = !!action.payload;
        },
        setExpanded(state, action) {
            state.expanded = !!action.payload;
        },
        toggleExpanded(state) {
            state.expanded = !state.expanded;
        },

        // ── Native plugin state mirror ──
        // Single reducer fed by NativePlaybackEngine's `playerState` event
        // (4Hz while playing + every transition + a resume snapshot on
        // app foreground). Atomically updates everything the UI needs to
        // mirror the native player.
        setNativePlayerState(state, action) {
            const p = action.payload || {};
            if (typeof p.positionSec === 'number') state.position = p.positionSec;
            if (typeof p.duration === 'number') state.duration = p.duration;
            if (typeof p.isPlaying === 'boolean') state.isPlaying = p.isPlaying;
            // The URL the plugin actually has loaded. The authoritative key
            // for "which track is this" on native — title+artist collide
            // across remixes and placeholder credits, and the play index
            // cannot be resolved against audioList (one entry on native).
            if (typeof p.url === 'string') state.currentUrl = p.url || null;
            if (typeof p.index === 'number' && p.index >= 0 && p.index !== state.currentPlayId) {
                state.currentPlayId = p.index;
            }
            // Mirror the native plugin's current track metadata into
            // `currentTrack` so the mini-player / NowPlayingPage show the
            // track that's actually playing — including ones the plugin
            // appended via lock-screen refill, which never landed in
            // audioList because JS was suspended.
            if (p.title || p.artist || p.artworkUrl) {
                const prev = state.currentTrack || {};
                // Resolve the playing track's music-info id from audioList so a
                // like fired from the now-playing overlay carries a
                // track_public_id — the feed's liked check matches by id first
                // (LikeButton), and native currentTrack otherwise has no id, so
                // the like was invisible in the feed. Desktop threads this id
                // directly; here we recover it by play index, then by
                // name+artist, then keep any prior id. Null id is fine (same as
                // before — falls back to the text key).
                const newName = p.title || prev.name || '';
                const newSinger = p.artist || prev.singer || '';
                const list = Array.isArray(state.audioList) ? state.audioList : [];
                // Only trust an entry whose name+artist match the now-playing
                // track — the play index can point at a stale/other entry (e.g.
                // lock-screen refills that never landed in audioList), and
                // attaching the WRONG id would like the wrong track. Prefer the
                // indexed entry when it matches, else search by name+artist.
                const matches = (t) => !!t && (t.name || '') === newName && (t.singer || '') === newSinger;
                const idx = (typeof p.index === 'number' && p.index >= 0)
                    ? p.index
                    : state.currentPlayId;
                const entry = (matches(list[idx]) ? list[idx] : null) || list.find(matches) || null;
                // Keep the prior id only when it's the same track (position-only
                // 4Hz updates); otherwise null → feed falls back to the text key
                // (same as before the fix — no regression, never a wrong id).
                const sameTrack = prev.name === newName && prev.singer === newSinger;
                const resolvedId = (entry && (entry.trackId || entry.id)) || (sameTrack ? (prev.id || null) : null);
                state.currentTrack = {
                    ...prev,
                    name: newName,
                    singer: newSinger,
                    cover: p.artworkUrl || prev.cover || '',
                    id: resolvedId,
                };
            }
        },

        // Load playlist from storage
        loadPlaylist(state) {
            try {
                const raw = localStorage.getItem('musicPlayer');
                if (raw) {
                    const saved = JSON.parse(raw);
                    if (saved.playMode) state.playMode = saved.playMode;
                    if (saved.mode) state.mode = saved.mode;
                    if (saved.currentPlayId) state.currentPlayId = saved.currentPlayId;
                    if (saved.autoRadio != null) state.autoRadio = saved.autoRadio;
                }
            } catch (error) {
                console.error('Failed to load playlist:', error);
            }
        },
    },
});

export const {
    setAudioList,
    addTrack,
    upgradeTrackSrc,
    upgradeTrackLink,
    syncQueueFromPlayer,
    removeTrack,
    resetAudioList,
    toggleAutoPlay,
    setAutoPlay,
    setPlayMode,
    setMode,
    toggleAutoRadio,
    setAutoRadio,
    addPlayedTrackKey,
    clearPlayedTracks,
    resetPlayerState,
    savePlaylist,
    loadPlaylist,
    setQueueOwner,
    setLikesPlayback,
    setLikesPlaybackOffset,
    setCurrentTrack,
    setIsPlaying,
    setExpanded,
    toggleExpanded,
    setNativePlayerState,
} = musicPlayerSlice.actions;

export default musicPlayerSlice.reducer;
